import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { AppLayout } from '../components/Layout';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { Award, Plus, Search, Star, Shield, Zap, Trophy, Heart, Cpu, Trash2, CheckCircle2, X, Sparkles } from 'lucide-react';
import confetti from 'canvas-confetti';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BADGE_ICONS = { star: Star, zap: Zap, shield: Shield, trophy: Trophy, heart: Heart, cpu: Cpu };
const BADGE_BG = {
  amber: 'from-amber-500 to-yellow-400', cyan: 'from-cyan-500 to-teal-400',
  emerald: 'from-emerald-500 to-green-400', gold: 'from-yellow-400 to-amber-500',
  blue: 'from-blue-500 to-indigo-400', red: 'from-red-500 to-rose-400',
};

const CLUSTER_COLORS = {
  it: 'bg-cyan-500/10 text-cyan-300 border-cyan-500/30',
  hlt: 'bg-red-500/10 text-red-300 border-red-500/30',
  arc: 'bg-amber-500/10 text-amber-300 border-amber-500/30',
  bus: 'bg-blue-500/10 text-blue-300 border-blue-500/30',
  mfg: 'bg-yellow-500/10 text-yellow-300 border-yellow-500/30',
  fin: 'bg-green-500/10 text-green-300 border-green-500/30',
  art: 'bg-purple-500/10 text-purple-300 border-purple-500/30',
  mkt: 'bg-rose-500/10 text-rose-300 border-rose-500/30',
  hos: 'bg-orange-500/10 text-orange-300 border-orange-500/30',
  edu: 'bg-sky-500/10 text-sky-300 border-sky-500/30',
  stem: 'bg-violet-500/10 text-violet-300 border-violet-500/30',
  law: 'bg-indigo-500/10 text-indigo-300 border-indigo-500/30',
};

function BadgeCard({ badge, earned = true }) {
  const Icon = BADGE_ICONS[badge.icon] || Star;
  const grad = BADGE_BG[badge.color] || BADGE_BG.amber;
  return (
    <motion.div
      whileHover={{ scale: earned ? 1.05 : 1 }}
      className={`rounded-xl p-4 text-center ${earned ? 'opacity-100' : 'opacity-30 grayscale'}`}
    >
      <div className={`w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br ${grad} flex items-center justify-center shadow-lg mb-2`}>
        <Icon className="w-8 h-8 text-white" />
      </div>
      <p className="text-white text-xs font-semibold">{badge.name}</p>
      <p className="text-slate-300 text-[10px] mt-0.5">{badge.desc}</p>
    </motion.div>
  );
}

function AddCredentialModal({ catalog, onClose, onAdded }) {
  const { getAuthHeader } = useAuth();
  const [step, setStep] = useState('search'); // search | confirm
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ earned_date: '', cert_number: '', expiry_date: '' });
  const [saving, setSaving] = useState(false);
  const [filtered, setFiltered] = useState(catalog);

  useEffect(() => {
    if (!q) { setFiltered(catalog); return; }
    const ql = q.toLowerCase();
    setFiltered(catalog.filter(c => c.name.toLowerCase().includes(ql) || c.issuer.toLowerCase().includes(ql)));
  }, [q, catalog]);

  const submit = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      const r = await axios.post(`${API}/credentials`, { ...selected, ...form }, getAuthHeader());
      onAdded(r.data.new_badge);
      onClose();
    } catch { toast.error('Could not add credential'); }
    finally { setSaving(false); }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 bg-[#0a1729]/60 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center p-4">
      <motion.div initial={{ y: 50, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 50, opacity: 0 }}
        className="w-full max-w-lg bg-slate-900 border border-slate-700 rounded-2xl overflow-hidden">

        <div className="flex items-center justify-between p-4 border-b border-slate-800">
          <h3 className="text-white font-semibold">
            {step === 'search' ? 'Add Credential' : `Add: ${selected?.name}`}
          </h3>
          <button onClick={onClose}><X className="w-5 h-5 text-slate-300 hover:text-white" /></button>
        </div>

        {step === 'search' ? (
          <div className="p-4 space-y-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-300" />
              <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search credentials..."
                autoFocus
                className="w-full pl-9 pr-4 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white placeholder-slate-500 focus:outline-none focus:border-cyan-500" />
            </div>
            <div className="max-h-72 overflow-y-auto space-y-1.5">
              {filtered.slice(0, 20).map(c => (
                <button key={c.id} onClick={() => { setSelected(c); setStep('confirm'); }}
                  className="w-full text-left px-3 py-2.5 rounded-lg hover:bg-slate-800 transition-colors flex items-center justify-between gap-3">
                  <div>
                    <p className="text-white text-sm font-medium">{c.name}</p>
                    <p className="text-slate-300 text-xs">{c.issuer}</p>
                  </div>
                  {c.nc_recognized && (
                    <span className="flex-shrink-0 text-[10px] px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-300 border border-amber-500/30">
                      NC IC
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div className="p-4 space-y-4">
            {selected?.nc_recognized && (
              <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20 flex items-center gap-2">
                <Star className="w-4 h-4 text-amber-400 flex-shrink-0" />
                <p className="text-amber-300 text-xs">NC-recognized Industry Credential, counts toward your Career Endorsement!</p>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-slate-300 mb-1 block">Date Earned</label>
                <input type="date" value={form.earned_date} onChange={e => setForm(f => ({...f, earned_date: e.target.value}))}
                  className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white" />
              </div>
              <div>
                <label className="text-xs text-slate-300 mb-1 block">Certificate # (optional)</label>
                <input value={form.cert_number} onChange={e => setForm(f => ({...f, cert_number: e.target.value}))}
                  className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white placeholder-slate-500"
                  placeholder="ABC-123456" />
              </div>
            </div>
            <div>
              <label className="text-xs text-slate-300 mb-1 block">Expiration Date (optional)</label>
              <input type="date" value={form.expiry_date} onChange={e => setForm(f => ({...f, expiry_date: e.target.value}))}
                className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white" />
            </div>
            <div className="flex gap-3">
              <button onClick={() => setStep('search')} className="flex-1 py-2.5 rounded-xl border border-slate-700 text-slate-300 text-sm hover:border-slate-500">Back</button>
              <button onClick={submit} disabled={saving}
                className="flex-1 py-2.5 rounded-xl bg-cyan-600 hover:bg-cyan-500 text-white font-semibold text-sm disabled:opacity-60" data-testid="add-cred-confirm-btn">
                {saving ? 'Saving...' : 'Add Credential'}
              </button>
            </div>
          </div>
        )}
      </motion.div>
    </motion.div>
  );
}

function BadgeUnlockedToast({ badge }) {
  const Icon = BADGE_ICONS[badge?.icon] || Star;
  const grad = BADGE_BG[badge?.color] || BADGE_BG.amber;
  return (
    <motion.div initial={{ scale: 0.5, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
      className="flex items-center gap-3 p-3">
      <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${grad} flex items-center justify-center flex-shrink-0`}>
        <Icon className="w-6 h-6 text-white" />
      </div>
      <div>
        <p className="text-white font-bold">Badge Unlocked!</p>
        <p className="text-slate-300 text-sm">{badge?.name}, {badge?.desc}</p>
      </div>
    </motion.div>
  );
}

export default function CredentialTrackerPage() {
  const { getAuthHeader } = useAuth();
  const [data, setData] = useState(null);
  const [catalog, setCatalog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const ALL_BADGE_TIERS = [
    {id:"first_cred",name:"First Step",desc:"Earned your first credential",icon:"star",color:"amber"},
    {id:"triple",name:"Triple Certified",desc:"Earned 3 credentials",icon:"zap",color:"cyan"},
    {id:"nc_ready",name:"NC Ready",desc:"Earned an NC-recognized IC",icon:"shield",color:"emerald"},
    {id:"tech_stack",name:"Tech Stack",desc:"2+ IT credentials",icon:"cpu",color:"blue"},
    {id:"health_hero",name:"Health Hero",desc:"2+ Health credentials",icon:"heart",color:"red"},
    {id:"endorsement_ready",name:"Endorsement Ready",desc:"Qualifying NC credential",icon:"trophy",color:"gold"},
  ];

  const load = useCallback(async () => {
    try {
      const [credsRes, catalogRes] = await Promise.all([
        axios.get(`${API}/credentials`, getAuthHeader()),
        catalog.length === 0 ? axios.get(`${API}/credentials/catalog`, getAuthHeader()) : null,
      ]);
      setData(credsRes.data);
      if (catalogRes) setCatalog(catalogRes.data.catalog || []);
    } catch { toast.error('Could not load credentials'); }
    finally { setLoading(false); }
  }, [getAuthHeader]);

  useEffect(() => { load(); }, []);

  const handleAdded = (newBadge) => {
    load();
    confetti({ particleCount: 120, spread: 80, origin: { y: 0.6 }, colors: ['#06b6d4','#8b5cf6','#f59e0b'] });
    if (newBadge) {
      toast.custom(() => <BadgeUnlockedToast badge={newBadge} />, { duration: 5000 });
    } else {
      toast.success('Credential added!');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this credential?')) return;
    try {
      await axios.delete(`${API}/credentials/${id}`, getAuthHeader());
      toast.success('Removed');
      load();
    } catch { toast.error('Could not remove'); }
  };

  const credentials = data?.credentials || [];
  const badges = data?.badges || [];
  const earnedBadgeIds = new Set(badges.map(b => b.id));

  return (
    <AppLayout>
      <div className="max-w-4xl mx-auto space-y-8 pb-12" data-testid="credential-tracker-page">

        {/* Header */}
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <Sparkles className="w-4 h-4 text-amber-400" />
                <span className="text-xs font-semibold uppercase tracking-widest text-amber-400">Achievement</span>
              </div>
              <h1 className="text-4xl font-bold text-white">Credential Tracker</h1>
              <p className="text-slate-300 text-sm mt-1">
                Earn industry credentials. Unlock badges. Qualify for the NC Career Endorsement diploma.
              </p>
            </div>
            <button onClick={() => setShowAdd(true)} data-testid="add-cred-btn"
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-cyan-600 hover:bg-cyan-500 text-white font-semibold shadow-lg shadow-cyan-900/30 transition-all hover:scale-105">
              <Plus className="w-4 h-4" /> Add Credential
            </button>
          </div>
        </motion.div>

        {/* Stats bar */}
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }}
          className="grid grid-cols-3 gap-4">
          {[
            { label: 'Credentials Earned', value: credentials.length, color: 'text-white' },
            { label: 'NC Industry Credentials', value: data?.nc_recognized_count || 0, color: 'text-amber-400' },
            { label: 'Endorsement Eligible', value: data?.endorsement_eligible ? 'Yes' : 'Not Yet', color: data?.endorsement_eligible ? 'text-emerald-400' : 'text-slate-300' },
          ].map((s, i) => (
            <div key={i} className="rounded-xl border border-slate-700/50 bg-slate-800/40 p-4 text-center">
              <p className={`text-3xl font-bold ${s.color}`}>{s.value}</p>
              <p className="text-slate-300 text-xs mt-1">{s.label}</p>
            </div>
          ))}
        </motion.div>

        {/* Badge wall */}
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.15 }}
          className="rounded-2xl border border-slate-700/50 bg-slate-800/30 p-5">
          <h2 className="text-white font-semibold mb-1">Badge Wall</h2>
          <p className="text-slate-300 text-xs mb-4">Earn credentials to unlock achievement badges</p>
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
            {ALL_BADGE_TIERS.map(badge => (
              <BadgeCard key={badge.id} badge={badge} earned={earnedBadgeIds.has(badge.id)} />
            ))}
          </div>
        </motion.div>

        {/* Credentials list */}
        {loading ? (
          <div className="flex justify-center py-12"><div className="w-6 h-6 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin" /></div>
        ) : credentials.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-20 h-20 mx-auto rounded-2xl bg-gradient-to-br from-amber-500/20 to-cyan-500/20 border border-amber-500/20 flex items-center justify-center mb-4">
              <Award className="w-10 h-10 text-amber-400/60" />
            </div>
            <p className="text-white font-semibold text-lg mb-1">No credentials yet</p>
            <p className="text-slate-300 text-sm mb-6">Add your first credential to start earning badges and qualifying for the NC Career Endorsement.</p>
            <button onClick={() => setShowAdd(true)} className="px-5 py-2.5 rounded-xl bg-cyan-600 hover:bg-cyan-500 text-white font-medium">
              Add Your First Credential
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            <h2 className="text-white font-semibold">Your Credentials ({credentials.length})</h2>
            {credentials.map((cred, i) => (
              <motion.div key={cred.id} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.05 }}
                className="rounded-xl border border-slate-700/50 bg-slate-800/40 p-4 flex items-center gap-4"
                data-testid="credential-card">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-slate-700 to-slate-800 border border-slate-600 flex items-center justify-center flex-shrink-0">
                  <Award className="w-6 h-6 text-amber-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-white font-semibold text-sm">{cred.name}</p>
                    {cred.nc_recognized && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-300 border border-amber-500/30 flex items-center gap-0.5">
                        <Star className="w-2.5 h-2.5" /> NC IC
                      </span>
                    )}
                    {cred.verified && <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />}
                  </div>
                  <p className="text-slate-300 text-xs">{cred.issuer}</p>
                  <div className="flex items-center gap-3 mt-1 text-xs text-slate-300 flex-wrap">
                    {cred.earned_date && <span>Earned: {cred.earned_date}</span>}
                    {cred.cert_number && <span>#{cred.cert_number}</span>}
                    {cred.expiry_date && <span>Expires: {cred.expiry_date}</span>}
                  </div>
                </div>
                <button onClick={() => handleDelete(cred.id)} className="text-slate-300 hover:text-red-400 transition-colors p-1">
                  <Trash2 className="w-4 h-4" />
                </button>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      <AnimatePresence>
        {showAdd && <AddCredentialModal catalog={catalog} onClose={() => setShowAdd(false)} onAdded={handleAdded} />}
      </AnimatePresence>
    </AppLayout>
  );
}
